<template>
  <profile-section
    section-title="Journey"
    :section-subtitle="sectionSubtitle"
  >
    <profile-subsection
      action-text="Edit"
      :editing="activeSection === 'Journey'"
      :disabled="activeSection && activeSection !== 'Journey'"
      :disabled-save="submitting"
      @active-action="submit"
      @inactive-action="$emit('set-active', 'Journey')"
      @cancel-action="$emit('clear-active')"
    >
      <template v-slot:inactive>
        <template v-for="profileConfig in profileConfigs">
          <p
            v-if="profileConfig.dataType === 'date'"
            :key="profileConfig.id"
            class="config-display"
          >
            {{ profileConfig.displayName }}: {{ configDisplay(profileConfig) }}
          </p>
          <template v-else>
            <button
              v-if="showTransitionButton(profileConfig)"
              :key="profileConfig.id"
              class="config-display transition-btn"
              @click="startTransition"
            >
              + Add test results
            </button>
            <p
              v-else
              :key="profileConfig.id"
              class="config-display"
            >
              {{ profileConfig.displayName }}: {{ configDisplay(profileConfig) }}
            </p>
          </template>
        </template>
      </template>
      <template v-slot:active>
        <template v-for="profileConfig in profileConfigs">
          <date-picker
            v-if="profileConfig.dataType === 'date'"
            :key="profileConfig.id"
            class="config-display"
            :date="dates[profileConfig.userProfileColumnName]"
            :label="`${profileConfig.displayName}:`"
            :custom-years="datepickerYears"
            @date-change="handleDateChange(profileConfig, ...arguments)"
          />
          <template v-else>
            <button
              v-if="showTransitionButton(profileConfig)"
              :key="profileConfig.id"
              class="config-display transition-btn active"
              @click="startTransition"
            >
              + Add test results
            </button>
            <p
              v-else
              :key="profileConfig.id"
              class="config-display active"
            >
              {{ profileConfig.displayName }}: {{ configDisplay(profileConfig) }}
            </p>
          </template>
        </template>
      </template>
    </profile-subsection>
  </profile-section>
</template>

<script>
import { format } from 'date-fns';
import Vue from 'vue';

import ProfileSection from '@components/profile/section';
import ProfileSubsection from '@components/profile/subsection';
import DatePicker from '@components/singletons/date-picker';

import AuthenticationMixin from '@/mixins/authentication';
import GlobalMixin from '@/mixins/global';

import { startTransitionQuestionnaire, transitionQuestionnaireId } from '@/utils/questionnaire';
import { parseApiDateString } from '@/utils';

export default {
  components: { ProfileSection, ProfileSubsection, DatePicker },
  mixins: [AuthenticationMixin, GlobalMixin],
  props: {
    activeSection: {
      type: String,
    },
  },
  data() {
    return {
      dates: {},
      status: {},
      submitting: false,
    };
  },
  computed: {
    datepickerYears() {
      const firstYear = this.years[0];
      const previousYears = [-3, -2, -1].map(n => n + firstYear);
      return [...previousYears, ...this.years];
    },
    userTransitionable() {
      // Only some user classifications should be able to transition
      // However, user profile configs are also used for displaying results
      // We can use the value of the transitionQuestionnaire to determine if they are transitionable
      // where only user classifications with a transition questionnaire id are transitionable
      return transitionQuestionnaireId(this.$store);
    },
    profileConfigs() {
      const classificationConfigs = this.$store.getters['userProfileConfigs/get'][this.user.classificationId];
      if (classificationConfigs) {
        return classificationConfigs.filter(config => config.userProfileColumnName !== 'studyGoal').sort((a, b) => a.order - b.order);
      }
    },
    sectionSubtitle() {
      const userClass = this.user.classification;
      const labels = {
        'nurse_rn': 'Registered Nurse',
        'student_rn': 'Nursing Student',
      };
      return labels[userClass];
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
    userProfile() {
      return this.user && this.user.userProfile || {};
    },
  },
  created() {
    this.$watch('userProfile', profile => {
      this.profileConfigs.forEach(config => {
        if (config.dataType === 'date') {
          if (this.userProfile[config.userProfileColumnName]) {
            const apiDate = parseApiDateString(this.userProfile[config.userProfileColumnName]);
            Vue.set(this.dates, config.userProfileColumnName, { month: apiDate.month, day: apiDate.day, year: apiDate.year });
          } else {
            Vue.set(this.dates, config.userProfileColumnName, { month: 0, day: 0, year: 0 });
          }
        } else if (config.dataType === 'status') {
          this.status[config.userProfileColumnName] = this.userProfile[config.userProfileColumnName];
        }
      });
    }, { immediate: true });
  },
  methods: {
    configDisplay(config) {
      if (config.dataType === 'date') {
        const key = config.userProfileColumnName;
        const dateEntered = Object.keys(this.dates[key]).some(k => this.dates[key][k] > 0);
        if (!dateEntered) {
          return 'MM/DD/YYYY';
        } else {
          const dateObj = new Date(this.dates[key].year, this.dates[key].month - 1, this.dates[key].day);
          return format(dateObj, 'MM/dd/yyyy');
        }
      } else if (config.dataType === 'status') {
        if (this.status[config.userProfileColumnName] === true) return 'Passed';
        if (this.status[config.userProfileColumnName] === false) return 'Unsuccessful';
        // If a classification is not transitionable, we assume they have already passed their exams
        // But we should only display passed if the test date is in the past, for consistency's sake
        if (!this.userTransitionable && this.previousDateInPast(config)) return 'Passed';
        return 'N/A';
      }
    },
    dateHasChanged(enteredDate, userProfileColumnName) {
      if (this.userProfile && this.userProfile[userProfileColumnName]) {
        const apiDate = parseApiDateString(this.userProfile[userProfileColumnName]);
        // both dates are in custom object format
        return apiDate.year !== enteredDate.year || apiDate.month !== enteredDate.month || apiDate.day !== enteredDate.day;
      } else {
        // user doesn't have a user profile yet so any changes are new
        return true;
      }
    },
    handleDateChange(dateConfig, key, value) {
      this.dates[dateConfig.userProfileColumnName][key] = value;
    },
    previousDateInPast(config) {
      // In the case of profile configs of type "status", we sometimes need to look back at the previous config
      // in order to see if the date entered in previous config has passed. Status configs are used for test results
      // and the previous config is the corresponding date of the test.
      const prevConfig = this.profileConfigs.find(c => c.order === config.order - 1);
      if (prevConfig) {
        // prevDateObj will get the values of the date inside the date picker rather than the values from the api
        // so that we are comparing with the most up to date user entered information. We also check if the year
        // and the day is !== 0 to ensure we are using a date that the user entered and not the default values.
        // If there is a user entered date, we compare the current date with a date object constructed from
        // the user entered values.
        const prevDateObj = this.dates[prevConfig.userProfileColumnName];
        const hasPrevDateValue = prevDateObj.year !== 0 && prevDateObj.day !== 0;
        if (prevDateObj && hasPrevDateValue) {
          return new Date() > new Date(prevDateObj.year, prevDateObj.month - 1, prevDateObj.day);
        }
      }
    },
    showTransitionButton(config) {
      const userHasResults = this.status[config.userProfileColumnName] === true || this.status[config.userProfileColumnName] === false;
      return this.previousDateInPast(config) && !userHasResults && this.userTransitionable;
    },
    submit(showNotification = true) {
      this.submitting = true;
      const params = { };

      this.profileConfigs.forEach(config => {
        if (config.dataType === 'date') {
          const enteredDate = this.dates[config.userProfileColumnName];
          if (enteredDate.month > 0 && enteredDate.year > 0) {
            params[config.userProfileColumnName] = `${enteredDate.year}-${enteredDate.month}-${enteredDate.day}`;
            if (this.dateHasChanged(enteredDate, config.userProfileColumnName)) {
              this.$analytics.events.updatedJourneyDate(params[config.userProfileColumnName], config.userProfileColumnName);
            }
          }
        }
      });

      if (Object.keys(params).length < 1) {
        this.$emit('clear-active');
        this.submitting = false;
        return;
      }

      this.$store.dispatch('session/updateUserProfile', params).then(response => {
        this.$emit('clear-active');
        this.submitting = false;
        if (showNotification) {
          this.$notify({
            group: 'notifications',
            text: 'Profile Updated',
            type: 'success',
          });
        }
      }).catch(e => {
        this.$emit('clear-active');
        this.submitting = false;
        if (showNotification) {
          this.$notify({
            group: 'notifications',
            text: 'Error Updating Profile',
            type: 'error',
          });
        }
      });
    },
    startTransition() {
      this.submit(false);
      startTransitionQuestionnaire.apply(this);
    },
  },
};
</script>

<style lang="scss" scoped>
  .config-display {
    &.transition-btn {
      color: #00d8f8;
      background: transparent;
      margin: rem-calc(16) 0;
    }

    &.active {
      margin: 0 0 0 rem-calc(32);
    }

    &.disabled {
      color: #989898;
    }

    @media (max-width: 1150px) {
      &.active {
        display: block;
        margin-left: rem-calc(106) !important;
      }
    }

    @include breakpoint(medium) {
      &:not(:first-child) {
        display: block;
      }

      &.active,
      &:nth-child(3n) {
        margin-left: 0 !important;
      }
    }
  }
</style>
